import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { paidLabel } from '../../components/Claims/Payment/paymentAdvice';
import { useLocation, useNavigate } from "react-router-dom";
import Draggable from "react-draggable";
import {
  Select, Button, Checkbox, Modal, Input, Spin, Row, Col, notification, Alert,
  Drawer, Typography, Space, Empty, Dropdown, Menu, Badge, Tag, Tooltip
} from "antd";
import {
  ArrowLeftOutlined, CheckOutlined, CloseOutlined,
  DeleteOutlined, FileTextOutlined, MessageOutlined, RollbackOutlined,
} from "@ant-design/icons";
import { BiBookOpen } from "react-icons/bi";
import CurrencyFormat from "react-currency-format";
import debounce from "lodash/debounce";

import { uri, uri_img } from "../../assets/utils/http-request";
import lib, { myRequestObj, removeDuplicates } from "../../assets/utils/lib";
import { add_approved, add_claim } from "../../stores/Claims";
import { add_queries, selectQueries } from "../../stores/queries";
// import logo from '../../assets/images/logo.png'
import logo from "../../assets/images/claima.png";
import "./claims.css";
import "./claims-action-dashboard.css";
import DashboardSection from "../dashboard/components/DashboardSection";
import { ADMIN_REVIEW_MODAL_CLASS, roundMoney } from "../dashboard/utils";
import { paLineItemName } from "../preauth/paDisplayUtils";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import config from "../../assets/utils/config";
import { renderField } from "../compoinents/renderField";
import PAItemFlagIndicator from "../compoinents/PAItemFlagIndicator";
import { apiRequest } from "../notify";
import { createItemFlagsApi, useEncounterItemFlags, ItemFlagTag, ItemFlagsList } from "../preauth/ItemFlags";

// Flags raised on PA items (facility, enrollee, staff) — shown to Claims on every line.
const itemFlagsApi = createItemFlagsApi(apiRequest);
import PACommentsDisplay from "../../components/Claims/PACommentsDisplay";
import ClaimLineDecisions from "../../components/Claims/ClaimLineDecisions";
import PmMappingPanel from "../../components/PlanManagement/PmMappingPanel";
import EnrolleeIdentityStrip from "../compoinents/EnrolleeIdentityStrip";
import ClaimDocumentsPanel, {
  resolvePaAttachmentUrl,
} from "./ClaimDocumentsPanel";
// import FileUploadModal from "../compoinents/uploader";

const { TextArea } = Input;

const ClaimsHook = () => {
  const { user } = useAuth();
  const { canStayOnModule, hasAnyPermissionWithLegacy, hasPermission } = usePermissions();
  // Revert a submitted claim back to an open encounter: administrators only.
  // The API checks admin.* again on its own, whatever the RBAC mode.
  const isAdministrator = hasPermission('admin.*');
  const navigate = useNavigate();

  // Claims workflow stage capabilities. The legacy ids mirror the server-side role checks
  // in api_v1/User.php:9329+ (forward-to-HM requires [1, 3], forward-to-CFO requires
  // [1, 4]) and apply only while this account's RBAC map is empty.
  const canProcessClaims = hasAnyPermissionWithLegacy(['claims.process'], [3]);
  const canApproveClaims = hasAnyPermissionWithLegacy(['claims.approve'], [4]);
  const canRejectClaims = hasAnyPermissionWithLegacy(['claims.reject'], [4]);
  // "Is this specifically the claims-officer stage actor?" The negative clause keeps a
  // super admin (admin.* expands to every claims permission) out of this bucket, which is
  // what the old `=== 3` literal did.
  const isClaimsOfficerStage = canProcessClaims
    && !hasAnyPermissionWithLegacy(['claims.approve', 'claims.review'], [4]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const itemFlagState = useEncounterItemFlags(itemFlagsApi, params.get("encounter"));
  const state = location.state;
  const dispatcher = useDispatch();

  // Utility function to detect circular references
  const detectCircularReferences = (obj, path = '') => {
    const seen = new WeakSet();

    const check = (obj, path = '') => {
      if (obj === null || typeof obj !== 'object') return;

      if (seen.has(obj)) {
        return true;
      }

      seen.add(obj);

      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const newPath = path ? `${path}.${key}` : key;
          if (check(obj[key], newPath)) {
            return true;
          }
        }
      }

      seen.delete(obj);
      return false;
    };

    return check(obj, path);
  };

  // Utility function to check if data can be serialized
  const canSerialize = (data) => {
    try {
      JSON.stringify(data);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Utility function to find problematic properties that prevent serialization
  const findProblematicProperties = (data, path = '') => {
    const problematic = [];

    const check = (obj, currentPath = '') => {
      if (obj === null || typeof obj !== 'object') return;

      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const newPath = currentPath ? `${currentPath}.${key}` : key;
          const value = obj[key];

          try {
            JSON.stringify(value);
          } catch (error) {
            problematic.push({
              path: newPath,
              value: value,
              type: typeof value,
              constructor: value?.constructor?.name,
              error: error.message
            });
          }

          if (typeof value === 'object' && value !== null) {
            check(value, newPath);
          }
        }
      }
    };

    check(data, path);
    return problematic;
  };

  const [items, setItems] = useState([]);
  // Line-level adjudication. The rows come from the legacy PAListAdminClaim
  // payload, which carries no mark state, so the decision bar fetches it from v2
  // and hands back a map keyed by claim_items.id_ to merge in here.
  const [lineSelection, setLineSelection] = useState([]);
  const [decisionMap, setDecisionMap] = useState({});
  const [response, setResponse] = useState({});
  // const [diagnosis, setDiagnosis] = useState([]);
  const [provDiagenosis, setProvDiagenosis] = useState([]);
  const [finalDiagenosis, setFinalDiagenosis] = useState([]);
  const [value, setValue] = useState([]);
  const [client, setClient] = useState({});
  const [encounter, setEncounter] = useState({});
  const [paHeaderAttachments, setPaHeaderAttachments] = useState(null);
  const [requestedCost, setRequestedCost] = useState(0);
  const [paApprovedCost, setPaApprovedCost] = useState(0);
  const [approvedCostD, setApprovedCostD] = useState(0);
  const [varianceCost, setVarianceCost] = useState(0);
  const computeClaimTotals = (claimItems = [], claimSummary = {}) => {
    const toFiniteNumber = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };
    const requestedFromSummary = toFiniteNumber(claimSummary?.requested_total);
    const paApprovedFromSummary = toFiniteNumber(claimSummary?.pa_approved_total);
    const claimsApprovedFromSummary = toFiniteNumber(claimSummary?.claims_approved_total);

    const fallbackTotals = (claimItems || []).reduce((acc, item) => {
      const requested = roundMoney(item?.cost || 0);
      const paApproved = roundMoney(item?.pa_approved_cost ?? item?.approved_cost ?? 0);
      const claimsApprovedRaw = toFiniteNumber(item?.claims_approved_cost);
      const claimsApproved = claimsApprovedRaw !== null ? roundMoney(claimsApprovedRaw) : 0;
      acc.requested = roundMoney(acc.requested + requested);
      acc.paApproved = roundMoney(acc.paApproved + paApproved);
      acc.claimsApproved = roundMoney(acc.claimsApproved + claimsApproved);
      acc.effectiveApproved = roundMoney(acc.effectiveApproved + claimsApproved);
      return acc;
    }, {
      requested: 0,
      paApproved: 0,
      claimsApproved: 0,
      effectiveApproved: 0,
      variance: 0,
      fromSummary: false,
    });

    const requested = roundMoney(requestedFromSummary !== null ? requestedFromSummary : fallbackTotals.requested);
    const paApproved = roundMoney(paApprovedFromSummary !== null ? paApprovedFromSummary : fallbackTotals.paApproved);
    const claimsApproved = roundMoney(claimsApprovedFromSummary !== null ? claimsApprovedFromSummary : fallbackTotals.claimsApproved);
    const effectiveApproved = claimsApprovedFromSummary !== null
      ? roundMoney(claimsApprovedFromSummary)
      : claimsApproved;

    return {
      requested,
      paApproved,
      claimsApproved,
      effectiveApproved,
      variance: roundMoney(paApproved - claimsApproved),
      fromSummary: requestedFromSummary !== null || paApprovedFromSummary !== null || claimsApprovedFromSummary !== null,
    };
  };

  // Upload modal controls
  const [rejectionMemo, setRejectionMemo] = useState("");
  const [approvalMemo, setApprovalMemo] = useState("");
  const [, setVisiblePaid] = useState(false);
  const [visibleMedicalComplaints, setVisibleMedicalComplaints] =
    useState(false);
  const [visibleReject, setVisibleReject] = useState(false);
  const [visibleApproval, setVisibleApproval] = useState(false);
  const [medicalComplaints, setMedicalComplaints] = useState([]);
  const [ApprovalComplaints, setApprovalComplaints] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const draggleRef = useRef(null);

  const [sending, setSending] = useState(false);
  const [revertInfo, setRevertInfo] = useState(null); // { revertible, reason }
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [reverting, setReverting] = useState(false);
  const [loading, setLoadding] = useState(true);
  const [loadError, setLoadError] = useState("");

  // const [showUpload, setShowUpload] = useState(false);
  const [complaint, setCompaint] = useState();
  const [updating, setUpdating] = useState(false);
  const [infoTab, setInfoTab] = useState('diagnosis');

  const interval = useRef();

  const handleCancel = (e) => {
    setVisiblePaid(false);
    setVisibleApproval(false)
    setVisibleReject(false);
    setVisibleMedicalComplaints(false);
  };

  const onStart = (_event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();

    if (!targetRect) {
      return;
    }

    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });

  // Submission Controls
  const [, setSubmittedHM] = useState(false);
  const [submittedCFO, setSubmittedCFO] = useState(false);

  const [canEdit, setCanEdit] = useState(false);
  const reviewRequired = Number(response?.review_required ?? 0);
  
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!canStayOnModule('claims', [1, 3, 4, 5])) {
      notification.warning({
        message: "Access Denied",
        description: "You don't have permission to process this claim.",
        placement: "topLeft",
      });
      navigate("/dashboard");
      return;
    }

      // For new admin flow, we can derive facility from claim data or encounter
      // Try to get facility from state, claim object, or we'll get it from encounter data
      let facilityId = state?.facility;
      let segment = state?.segment;

      // Try to get facility from claim object if passed in state
      if (!facilityId && state?.claim?.facility_id) {
        facilityId = state.claim.facility_id;
      }

      // Try to get segment from claim status if not provided
      if (segment === undefined || segment === null) {
        if (state?.claim?.claims_status !== undefined) {
          segment = state.claim.claims_status;
        } else if (state?.claim?.flag?.code !== undefined) {
          segment = state.claim.flag.code;
        } else {
          segment = 0; // Default to Pending CO
        }
      }

      // Store facility and segment for use throughout the component
      // We'll update facility from encounter data if it's not available yet
      if (facilityId) {
        // Store in a way that can be accessed later
        window.__claimsFilingFacility = facilityId;
        window.__claimsFilingSegment = segment;
      }

      // If we don't have facility yet, we'll try to get it from encounter data after fetching
      // Don't block navigation - we'll handle it in the fetchPAData callback
      if (!facilityId) {
        console.warn('Facility ID not provided in state, will attempt to derive from encounter data');
      }
    // return () => clearInterval(interval.current);
  }, [user, canStayOnModule, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!params.get("encounter")) navigate("/claims");
    setLoadError("");
    let loadFailed = false;
    // setLoadding(true);
    fetchPAData(params.get("encounter"))
      .then((res) => {
        setItems(res?.items);
        setPaHeaderAttachments(
          Array.isArray(res?.pa_header_attachments) ? res.pa_header_attachments : null,
        );
        const summary = {
          ...(res?.claim_financials || {}),
          ...(res?.claim?.claim_financials || {}),
          ...(res?.claim || {}),
        };
        const totals = computeClaimTotals(res?.items, summary);
        setRequestedCost(totals.requested || 0);
        setPaApprovedCost(totals.paApproved || 0);
        setApprovedCostD(totals.claimsApproved || 0);
        setVarianceCost(totals.variance || 0);
        setClient(res?.misc);
        setEncounter(res?.encounter);
        setCompaint(res?.misc?.medical_complaint)
        dispatcher(add_claim(res?.items));
        setResponse(res?.claim || {});

        // If we don't have facility from state, try to get it from encounter or claim data
        if (!window.__claimsFilingFacility) {
          const facilityFromEncounter = res?.encounter?.facility_id || res?.encounter?.facilityId;
          const facilityFromClaim = res?.claim?.facility_id || res?.claim?.facilityId;
          const facilityId = facilityFromEncounter || facilityFromClaim;

          if (facilityId) {
            window.__claimsFilingFacility = facilityId;
            // If we don't have segment, try to get it from claim
            if (!window.__claimsFilingSegment) {
              const segmentFromClaim = res?.claim?.claims_status !== undefined
                ? res.claim.claims_status
                : (res?.claim?.flag?.code !== undefined ? res.claim.flag.code : 0);
              window.__claimsFilingSegment = segmentFromClaim;
            }
          }
        }
      })
      .catch((error) => {
        loadFailed = true;
        console.error("Failed to load claim action data:", error);
        setLoadError(error?.message || "Unable to load claim details right now.");
      })
      .finally(() => {
        setLoadding(false);
        if (!loadFailed) {
          updateList(false)
        }
      });
    return () => clearInterval(interval.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (response && Object.keys(response).length) {
      // All HM comments (including medical) now come from claims_comment
      const rawComments =
        typeof response["claims_comment"] === 'string'
          ? response["claims_comment"]
          : '';

      const allComments = rawComments
        .split("|break|")
        .map((el) => el.trim())
        .filter((el) => el);

      const medicalPrefix = 'medical comment';
      const medical = [];
      const general = [];

      allComments.forEach((c) => {
        if (c.toLowerCase().startsWith(medicalPrefix)) {
          medical.push(c);
        } else {
          general.push(c);
        }
      });

      setApprovalComplaints(general);
      setMedicalComplaints(medical);
    }
  }, [response]);

  async function fetchDiagnosisList(username) {
    return myRequestObj(`diagnosisList`)
      .post({ search: username })
      .then((response) => response?.data)
      .then((body) => {
        return body?.data?.map((user) => ({
          label: `${user?.code} ${user?.name}`,
          value: user?.id,
        }));
      });
  }

  async function fetchPAData(claimsId) {
    if (!claimsId) return {}
    return myRequestObj(`PAListAdminClaim`)
      .post({ checkinId: claimsId })
      .then((response) => response?.data)
      .then((body) => {
        if (!body || body?.status) {
          throw new Error(body?.message || "Claim data is unavailable.");
        }
        const provList = (body?.data?.diagnosis?.provisional || []).map((d) => ({
          label: `${d?.code} ${d?.name}`,
          value: d?.id,
        }));
        const finalList = (body?.data?.diagnosis?.final || []).map((d) => ({
          label: `${d?.code} ${d?.name}`,
          value: d?.id,
        }));
        setProvDiagenosis(provList);
        setFinalDiagenosis(finalList)
        setValue(d => {
          return removeDuplicates([...(d || []), ...finalList], 'value');
        })
        return body?.data;
      })
  }

  const addFinalDiagnosis = (e) => {
    setValue(e);
  };

  const send_claim_medical = () => {
    setSending(true);
    setVisibleApproval(false);

    // Create a clean payload with only serializable data
    const cleanPayload = {
      checkin_id: params.get("encounter"),
      claims_status: 1,
      sender: user.staffId,
      comment: approvalMemo,
    };

    submitAction(cleanPayload).then((data) => {
      setSubmittedHM(true);
      if (data?.error) {
        notification.error({
          message: data?.message,
          placement: "topLeft",
        });
      } else {
        notification.success({
          message: data?.message,
          placement: "topLeft",
        });
        setSending(false);
        
        // Helper to get facility ID from various formats
        const getFacilityId = (fac) => {
          if (!fac) return null;
          if (typeof fac === 'object') return fac.facility_id || fac.id || null;
          return fac;
        };
        
        // Helper to get month key from various formats
        const getMonthKey = (m) => {
          if (!m) return null;
          if (typeof m === 'string') return m;
          return m.month || m.key || null;
        };
        
        // Try multiple sources for facility (in order of preference)
        let facility = state?.facility || window.__claimsFilingFacility;
        if (!facility && encounter?.facility_id) {
          facility = encounter.facility_id;
        }
        if (!facility && encounter?.facilityId) {
          facility = encounter.facilityId;
        }
        if (!facility && response?.facility_id) {
          facility = response.facility_id;
        }
        if (!facility && response?.facilityId) {
          facility = response.facilityId;
        }
        
        // Try multiple sources for segment (in order of preference)
        let segment = state?.segment;
        if (segment === undefined || segment === null) {
          segment = window.__claimsFilingSegment;
        }
        if (segment === undefined || segment === null && response?.claims_status !== undefined) {
          segment = response.claims_status;
        }
        if (segment === undefined || segment === null && response?.flag?.code !== undefined) {
          segment = response.flag.code;
        }
        if (segment === undefined || segment === null) {
          segment = 0; // Default to Pending CO
        }
        
        // Source queue segment (where the user came from). After forwarding, return here so they can continue the queue.
        const sourceSegment = segment;
        
        const month = state?.month;
        
        // If month exists in state, use new Payment/Admin UI with URL parameters
        if (month) {
          const monthKey = getMonthKey(month);
          const facilityId = getFacilityId(facility);
          
          if (monthKey && facilityId !== null && facilityId !== undefined) {
            // Return to the source queue (not the destination segment)
            navigate(`/claims?status=${sourceSegment}&month=${monthKey}&facility=${facilityId}`);
            return;
          }
        }
        
        // Fallback to legacy route structure for backward compatibility
        // Only navigate if we have valid facility and segment
        const facilityId = getFacilityId(facility);
        if (facilityId !== null && facilityId !== undefined && segment !== null && segment !== undefined) {
          // Legacy route expects base64-encoded values
          try {
            navigate(`/claims/filings/${btoa(facilityId.toString())}/${btoa(segment.toString())}`);
          } catch (e) {
            // If encoding fails, fall back to base claims page
            console.error('Error encoding facility/segment:', e);
            navigate('/claims');
          }
        } else {
          // If we don't have facility/segment, just go to base claims page
          navigate('/claims');
        }
      }
    });
  };

  const revoke_sent_claim = () => {
    // Create a clean payload with only serializable data
    const cleanPayload = {
      checkin_id: params.get("encounter"),
      claims_status: 0,
      items: items?.map((i) => i.id_),
      purpose: "revoke",
    };

    submitAction(cleanPayload).then((data) => {
      setSubmittedHM(true);
      if (data?.error) {
        notification.error({
          message: data?.message,
          placement: "topLeft",
        });
      } else {
        notification.success({
          message: data?.message,
          placement: "topLeft",
        });
        
        const facility = state?.facility || window.__claimsFilingFacility;
        const month = state?.month;
        
        // Helper to get facility ID from various formats
        const getFacilityId = (fac) => {
          if (!fac) return null;
          if (typeof fac === 'object') return fac.facility_id || fac.id || null;
          return fac;
        };
        
        // Helper to get month key from various formats
        const getMonthKey = (m) => {
          if (!m) return null;
          if (typeof m === 'string') return m;
          return m.month || m.key || null;
        };
        
        // If month exists in state, use new Payment/Admin UI with URL parameters
        if (month) {
          const monthKey = getMonthKey(month);
          const facilityId = getFacilityId(facility);
          const newSegment = 0; // After revoking, status goes back to 0 (Pending CO)
          
          if (monthKey && facilityId !== null && facilityId !== undefined) {
            // Navigate back to Payment/Admin UI with URL parameters
            navigate(`/claims?status=${newSegment}&month=${monthKey}&facility=${facilityId}`);
            return;
          }
        }
        
        // Fallback to page reload for legacy UI
        window.location.reload();
      }
    });
  };

  const send_claim_finance = () => {
    setSending(true);

    // Create a clean payload with only serializable data
    const cleanPayload = {
      checkin_id: params.get("encounter"),
      claims_status: 2,
      claims_queried: 2,
    };

    submitAction(cleanPayload).then((data) => {
      setSubmittedCFO(true);
      setSending(false);
      
      const facility = state?.facility || window.__claimsFilingFacility;
      const month = state?.month;

      // Source queue segment (where the user came from). After forwarding, return here so they can continue the queue.
      let sourceSegment = state?.segment;
      if (sourceSegment === undefined || sourceSegment === null) {
        sourceSegment = window.__claimsFilingSegment;
      }
      if (sourceSegment === undefined || sourceSegment === null) {
        // HM forwards to CFO; default source queue is Pending HM
        sourceSegment = 1;
      }
      
      // Helper to get facility ID from various formats
      const getFacilityId = (fac) => {
        if (!fac) return null;
        if (typeof fac === 'object') return fac.facility_id || fac.id || null;
        return fac;
      };
      
      // Helper to get month key from various formats
      const getMonthKey = (m) => {
        if (!m) return null;
        if (typeof m === 'string') return m;
        return m.month || m.key || null;
      };
      
      // If month exists in state, use new Payment/Admin UI with URL parameters
      if (month) {
        const monthKey = getMonthKey(month);
        const facilityId = getFacilityId(facility);
        
        if (monthKey && facilityId !== null && facilityId !== undefined) {
          // Return to the source queue (not the destination segment)
          navigate(`/claims?status=${sourceSegment}&month=${monthKey}&facility=${facilityId}`);
          return;
        }
      }
      
      // Fallback to base claims listing
      navigate("/claims");
    });
  };

  const reject = () => {
    // Create a clean payload with only serializable data
    const cleanPayload = {
      checkin_id: params.get("encounter"),
      claims_status: 0,
      items: items?.map((i) => i.id_),
      comment: rejectionMemo,
      claims_rejected: 0,
      // claims_rejected: 1,
      sender: user.staffId,
    };

    submitAction(cleanPayload)
      .then((data) => {
        if (data?.error) {
          notification.error({
            message: data?.message || 'Rejection failed',
            placement: "topLeft",
          });
          setVisibleReject(false);
        } else {
          notification.success({
            message: data?.message || 'Claim rejected successfully',
            placement: "topLeft",
          });
          setVisibleReject(false);
          // Navigate back to claims list instead of full page reload
          // This prevents blank page issues
          try {
            // Role 3 is the Claims Officer (NOT Medical) per the seeded staff role map.
            // TODO(rbac-migration): the segment picked here therefore looks inverted —
            // a Claims Officer works segment 0 (Pending CO), not segment 1 (Pending HM).
            // Behaviour preserved verbatim through the permission migration; the fix is a
            // separate, deliberate product change.
            const segment = isClaimsOfficerStage ? 1 : 0;
            navigate(`/claims?status=${segment}`);
          } catch (navError) {
            console.error('Navigation error after rejection:', navError);
            // Fallback to dashboard if navigation fails
            navigate('/dashboard');
          }
        }
      })
      .catch((error) => {
        console.error('Error rejecting claim:', error);
        notification.error({
          message: 'Error',
          description: error?.message || 'Failed to reject claim',
          placement: "topLeft",
        });
        setVisibleReject(false);
      });
  };

  async function submitAction(clean) {
    return myRequestObj(`claimsActions`)
      .post(clean)
      .then((response) => {
        return response?.data;
      })
      .then((body) => {
        return body;
      })
      .catch((error) => {
        throw error;
      });
  }

  const updateList = (callOnce = true) => {
    if (interval.current) clearInterval(interval.current);
    // let isFetching = false;
    const request = () => fetchPAData(params.get("encounter")).then((res) => {
      // Clean the items to prevent circular references
      const cleanItems = res?.items?.map(item => {
        const cleanItem = { ...item };
        // Remove any potential React/DOM references
        delete cleanItem.__reactFiber$;
        delete cleanItem.__reactInternalInstance$;
        delete cleanItem._reactInternalInstance;
        delete cleanItem._reactInternalFiber;
        return cleanItem;
      }) || [];

      // Clean misc data to prevent circular references
      const cleanMisc = res?.misc ? { ...res.misc } : {};
      delete cleanMisc.__reactFiber$;
      delete cleanMisc.__reactInternalInstance$;
      delete cleanMisc._reactInternalInstance;
      delete cleanMisc._reactInternalFiber;

      // Clean claim data to prevent circular references
      const cleanClaim = res?.claim ? { ...res.claim } : {};
      delete cleanClaim.__reactFiber$;
      delete cleanClaim.__reactInternalInstance$;
      delete cleanClaim._reactInternalInstance;
      delete cleanClaim._reactInternalFiber;

      setItems(cleanItems);
      setItems(res?.items);
      if (Array.isArray(res?.pa_header_attachments)) {
        setPaHeaderAttachments(res.pa_header_attachments);
      }
      const summary = {
        ...(res?.claim_financials || {}),
        ...(res?.claim?.claim_financials || {}),
        ...(res?.claim || {}),
      };
      const totals = computeClaimTotals(res?.items, summary);
      setRequestedCost(totals.requested || 0);
      setPaApprovedCost(totals.paApproved || 0);
      setApprovedCostD(totals.claimsApproved || 0);
      setVarianceCost(totals.variance || 0);
      setClient(cleanMisc);
      dispatcher(add_claim(cleanItems));
      setResponse(cleanClaim || {});
      setClient(res?.misc);
      dispatcher(add_claim(res?.items));
      setResponse(res?.claim || {});
    }).finally(() => {
      // isFetching = false
    });
    if (callOnce) {
      request()
    } else {
      interval.current = setInterval(() => {
        // isFetching = true;
        if (window.location.pathname === "/claims-action") {
          request();
        }
      }, 5000);
    }
  };

  // Uploader controls
  const saveClaimDetails = () => {
    const form = new FormData();
    form.append('diagnosis', [...value.map((v) => v.label.split(' ')[0])]);
    form.append('careType', client?.checkinType);
    form.append('checkinId', params.get('encounter'));
    form.append('careCategory', null);
    if (complaint != null) {
      form.append('complaint', complaint);
    }

    setUpdating(true);
    myRequestObj('paLog_manual_update').post(form)
      .then((res) => res?.data)
      .then((data) => {
        if (Number(data?.error) === 1) {
          notification.error({ message: data?.message });
        } else if (Number(data?.error) === 0) {
          notification.success({ message: data?.message || 'Encounter details saved' });
        }
      }).finally(() => {
        updateList();
        setUpdating(false);
      });
  };

  // Ask the API whether this claim can still be reverted (Pending CO: not sent
  // to HM, not paid, not batched, not archived). Admins only.
  const encounterForRevert = params.get("encounter");
  const claimsStatusForRevert = response?.claims_status;
  useEffect(() => {
    if (!isAdministrator || !encounterForRevert) {
      setRevertInfo(null);
      return undefined;
    }
    let cancelled = false;
    lib.getClaimRevertStatus(encounterForRevert).then((resp) => {
      if (cancelled) return;
      const parsed = lib.parseApiEnvelope(resp);
      setRevertInfo(parsed.ok ? parsed.data : null);
    });
    return () => { cancelled = true; };
  }, [isAdministrator, encounterForRevert, claimsStatusForRevert]);

  const submitRevert = async () => {
    const reason = revertReason.trim();
    if (reason.length < 5) {
      notification.warning({ message: 'Give a reason (at least 5 characters)' });
      return;
    }
    setReverting(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.revertClaim(encounterForRevert, reason));
      if (!parsed.ok) {
        notification.error({ message: 'Claim not reverted', description: parsed.message });
        return;
      }
      notification.success({ message: 'Claim reverted', description: parsed.message });
      setRevertOpen(false);
      setRevertReason('');
      goBack();
    } finally {
      setReverting(false);
    }
  };

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    const month = state?.month;
    let facility = state?.facility;

    if (!facility && state?.claim?.facility_id) {
      facility = state.claim.facility_id;
    }

    if (!facility) {
      facility = window.__claimsFilingFacility;
    }

    const getFacilityId = (fac) => {
      if (!fac) return null;
      if (typeof fac === 'object') return fac.facility_id || fac.id || null;
      return fac;
    };

    const getMonthKey = (m) => {
      if (!m) return null;
      if (typeof m === 'string') return m;
      return m.month || m.key || null;
    };

    if (month && facility) {
      const monthKey = getMonthKey(month);
      const facilityId = getFacilityId(facility);
      let segment = state?.segment;
      if (segment === undefined || segment === null) {
        segment = window.__claimsFilingSegment;
      }
      if (segment === undefined || segment === null) {
        segment = 0;
      }

      if (monthKey && facilityId !== null && facilityId !== undefined) {
        navigate(`/claims?status=${segment}&month=${monthKey}&facility=${facilityId}`);
      } else {
        navigate('/claims', {
          state: {
            month,
            facility: typeof facility === 'object' ? facility : facility,
            segment,
          },
        });
      }
    } else {
      navigate('/claims');
    }
  };

  const facilityName = client?.facility_name || client?.facilityName || encounter?.facility_name || '--';
  const encounterId = params.get('encounter');

  // Test subjects (TEST-SUBJECTS): processed and paid normally, left out of operational figures.
  const [testStatus, setTestStatus] = useState(null);
  useEffect(() => {
    let live = true;
    if (!encounterId) return undefined;
    lib.getEncounterTestStatus(encounterId).then((st) => {
      if (live) setTestStatus(st?.is_test ? st : null);
    });
    return () => {
      live = false;
    };
  }, [encounterId]);
  const testVia = (testStatus?.via || [])
    .map((v) => `${{ facility: 'facility', policy: 'policy', organisation: 'organisation', enrollee: 'enrollee' }[v.type] || v.type} ${v.key}`)
    .join(', ');
  const claimStatusLabels = {
    0: 'Pending CO',
    1: 'Pending HM',
    2: 'Pending CFO',
    3: 'Paid',
  };
  const claimStatusLabel = Number(response?.claims_status) === 3
    ? paidLabel(response)
    : claimStatusLabels[Number(response?.claims_status)] || 'In review';

  const filingSourceTag = (() => {
    const mode = String(
      response?.source_mode ||
      (Number(response?.source) === 1 ? 'manual' : 'direct')
    ).toLowerCase();
    const manualCount = Number(response?.manual_items_count || 0);
    const directCount = Number(response?.direct_items_count || 0);
    const label = mode === 'mixed'
      ? `Mixed${manualCount || directCount ? ` (M:${manualCount} D:${directCount})` : ''}`
      : (mode === 'manual' ? 'Manual' : 'Direct');
    const color = mode === 'mixed' ? 'purple' : (mode === 'manual' ? 'orange' : 'green');
    return { label, color };
  })();

  const canDecideLines = canProcessClaims || canApproveClaims || canRejectClaims;

  const handleDecisionState = useCallback(({ decisions }) => {
    setDecisionMap(decisions || {});
  }, []);

  const decoratedItems = useMemo(
    () => (items || []).map((it) => ({ ...it, ...(decisionMap[String(it?.id_)] || {}) })),
    [items, decisionMap],
  );

  const decidableLineIds = useMemo(
    () => decoratedItems.filter((it) => it?.claim_decidable).map((it) => it.id_),
    [decoratedItems],
  );

  const allDecidableSelected =
    decidableLineIds.length > 0 && decidableLineIds.every((id) => lineSelection.includes(id));

  const toggleLineSelection = useCallback((id) => {
    setLineSelection((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // A line that stops being decidable (someone else applied, the claim reloaded)
  // must not stay selected, or the toolbar offers an action that would be refused.
  useEffect(() => {
    setLineSelection((prev) => prev.filter((id) => decidableLineIds.includes(id)));
  }, [decidableLineIds]);

  const careDiagnosisListHandler = (items) => {
    return Array.isArray(items)
      ? items.map((item) => {
        return {
          label: (
            <>
              <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                <strong>Name: </strong>
                {item?.name}
              </Typography.Paragraph>
              <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                <strong>Code: </strong>
                {item?.code}
              </Typography.Paragraph>
              {item?.description && (
                <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                  <strong>Description: </strong>
                  {item?.description}
                </Typography.Paragraph>
              )}
            </>
          ),
          value: item?.id,
        };
      })
      : items;
  };

  const documentsBadge =
    (Array.isArray(paHeaderAttachments) ? paHeaderAttachments.length : 0) +
    (items || []).filter((it) => it?.doc || it?.has_documents || (Array.isArray(it?.doc_urls) && it.doc_urls.length)).length;

  const hasPaBatchComment = Boolean(
    response?.pa_comment || encounter?.pa_comment ||
    response?.batch_comment || response?.pa_batch_comment || response?.pa_request_comment ||
    encounter?.batch_comment
  );
  const commentsBadge =
    (medicalComplaints?.length || 0) +
    (ApprovalComplaints?.length || 0) +
    (items || []).filter((it) => it?.claim_comment && it.claim_comment !== '').length +
    (response?.claims_comment && response.claims_comment !== '' ? 1 : 0) +
    (hasPaBatchComment ? 1 : 0);

  // Display label only (rendered in the "Signed in as ..." hint) — gates nothing.
  // Labels corrected to the seeded staff role map: 1 Super User, 3 Claims Officer,
  // 4 Head Of Medicals, 5 Head of Finance. Confirmed by api_v1 User.php
  // updateClaimForClaimProcessing: forward-to-HM requires roleId in [1,3] and logs
  // "Claim Approved by CO."; forward-to-CFO requires roleId in [1,4] and logs
  // "Claim Approved by HM.". The previous map was shifted one slot.
  // Stage label derived from what the signed-in user may actually do in the claims
  // workflow. Order matters: the broadest (admin.*) is tested first so a super admin does
  // not read as "Finance (CFO)".
  const workflowRoleLabel =
    hasAnyPermissionWithLegacy(['admin.*'], [1]) ? 'Super User'
      : isClaimsOfficerStage ? 'Claims Officer'
        : hasAnyPermissionWithLegacy(['claims.approve', 'claims.review'], [4]) ? 'Medical (HM)'
          : hasAnyPermissionWithLegacy(['claims.payment'], [5]) ? 'Finance (CFO)'
            : user?.role?.name || 'Reviewer';

  return (
    <>
      <div className="claims-action-dashboard">
        {loading ? (
          <div className="claims-action-loading">
            <Spin size="large" />
          </div>
        ) : loadError ? (
          <div className="dashboard-page-stack">
            <DashboardSection
              id="claim-filing-error"
              icon={<CloseOutlined />}
              title="Unable to load claim"
              subtitle="The server did not return claim data. Try again or return to claims."
              collapsible={false}
              variant="content"
            >
              <Alert
                type="error"
                showIcon
                message="Claim data could not be loaded"
                description={loadError}
                style={{ marginBottom: 16 }}
              />
              <Space>
                <Button type="primary" onClick={() => window.location.reload()}>
                  Retry
                </Button>
                <Button onClick={() => navigate("/claims")}>
                  Back to claims
                </Button>
              </Space>
            </DashboardSection>
          </div>
        ) : (
          <div className="dashboard-page-stack">
            <header className="dash-masthead">
              <div className="dash-masthead__lead">
                <h1 className="dash-masthead__title">Claim review</h1>
                <p className="dash-masthead__meta">Review line items, amounts, and workflow decisions</p>
              </div>
              <div className="dash-masthead__actions">
                {testStatus ? (
                  <Tooltip title={`Test subject (${testVia}). Process it normally (including payment); it is left out of Aman's operational figures and reports.`}>
                    <Tag color="default" style={{ marginInlineEnd: 0 }}>Test</Tag>
                  </Tooltip>
                ) : null}
                {encounterId ? (
                  <span className="dash-masthead__code">{encounterId}</span>
                ) : null}
                <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
                  Back
                </Button>
              </div>
            </header>

            {(reviewRequired === 1 || reviewRequired === 2) && medicalComplaints?.length > 0 && (
              <div className="claims-action-alert" role="alert">
                <span className="claims-action-alert__title">
                  {reviewRequired === 1 ? 'Returned by HM' : 'Awaiting HM review'}
                </span>
                <span className="claims-action-alert__body">
                  {medicalComplaints[medicalComplaints.length - 1]?.replace(/>>>/g, "→")}
                </span>
              </div>
            )}

            <EnrolleeIdentityStrip
              ariaLabel="Claim overview"
              client={{
                ...client,
                facility_name: facilityName,
                checkin_date: client?.checkin_date || encounter?.checkin_date,
                insurance_no: client?.insurance_no || client?.insuranceNo || encounter?.insurance_no,
              }}
              facilityName={facilityName}
              datetime={client?.checkin_date || encounter?.checkin_date}
              itemCount={(items || []).length}
              statusChip={claimStatusLabel}
              actions={(
                (response?.source_mode || response?.source !== undefined) ? (
                  <Tag color={filingSourceTag.color}>{filingSourceTag.label}</Tag>
                ) : null
              )}
            />

            <div className="claims-review-grid">
              <div className="claims-review-main">
                <div className="claims-info-card">
                  <div className="claims-info-card__tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={infoTab === 'diagnosis'}
                      className={`claims-info-card__tab${infoTab === 'diagnosis' ? ' is-active' : ''}`}
                      onClick={() => setInfoTab('diagnosis')}
                    >
                      Diagnosis
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={infoTab === 'documents'}
                      className={`claims-info-card__tab${infoTab === 'documents' ? ' is-active' : ''}`}
                      onClick={() => setInfoTab('documents')}
                    >
                      Documents
                      {documentsBadge > 0 ? <span className="claims-info-card__tab-badge">{documentsBadge}</span> : null}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={infoTab === 'comments'}
                      className={`claims-info-card__tab${infoTab === 'comments' ? ' is-active' : ''}`}
                      onClick={() => setInfoTab('comments')}
                    >
                      Comments
                      {commentsBadge > 0 ? <span className="claims-info-card__tab-badge">{commentsBadge}</span> : null}
                    </button>
                  </div>
                  <div className="claims-info-card__body">
                    {infoTab === 'diagnosis' ? (
                      <div className="claims-action-form-grid">
                        <div className="claims-action-field">
                          <label htmlFor="claim-medical-complaints">Medical complaints</label>
                          <TextArea
                            id="claim-medical-complaints"
                            rows={3}
                            value={complaint}
                            onChange={(e) => setCompaint(e.target.value)}
                            disabled={!Number(client?.source)}
                            placeholder="Enter patient's medical complaints..."
                          />
                        </div>

                        <div className="claims-action-field">
                          <label htmlFor="claim-final-diagnosis">Diagnosis — final</label>
                          <DebounceSelect
                            id="claim-final-diagnosis"
                            mode="multiple"
                            value={value}
                            placeholder="Select final diagnosis..."
                            fetchOptions={fetchDiagnosisList}
                            onChange={addFinalDiagnosis}
                            style={{ width: '100%' }}
                            optionHandler={careDiagnosisListHandler}
                            disabled={!Number(client?.source)}
                            maxTagCount={6}
                          />
                        </div>

                        {Number(client?.source) === 1 && (
                          <div className="claims-action-field">
                            <Button
                              type="primary"
                              size="small"
                              loading={updating}
                              disabled={updating}
                              onClick={saveClaimDetails}
                            >
                              Save encounter details
                            </Button>
                          </div>
                        )}

                        {Number(client?.source) === 1 && (
                          <Alert
                            type="info"
                            showIcon
                            message="Supporting documents"
                            description="Upload lab results, referrals, and other proof during PA filing (evidence step), not at checkout. Checkout only records complaints and diagnosis once PA is approved and evidence is complete."
                          />
                        )}
                      </div>
                    ) : null}

                    {infoTab === 'documents' ? (
                      <ClaimDocumentsPanel
                        checkinId={params.get("encounter")}
                        attachments={paHeaderAttachments}
                        lineItems={items}
                        embedded
                      />
                    ) : null}

                    {infoTab === 'comments' ? (
                      <PACommentsDisplay
                        response={response}
                        encounter={encounter}
                        showFullHistory={true}
                        compact={true}
                      />
                    ) : null}
                  </div>
                </div>

                <DashboardSection
                  id="claim-line-items"
                  icon={<FileTextOutlined />}
                  title="Line items"
                  subtitle="Review filed costs against PA and claims approvals"
                  collapsible={false}
                  variant="content"
                >
                  <div className="claims-action-field" style={{ marginBottom: 12 }}>
                    <label className="form-label">Diagnosis — provisional</label>
                    <Select
                      mode="multiple"
                      value={provDiagenosis}
                      placeholder="No provisional diagnosis"
                      style={{ width: '100%' }}
                      disabled
                    />
                  </div>
                  <ClaimLineDecisions
                    checkinId={params.get("encounter")}
                    selectedIds={lineSelection}
                    onClearSelection={() => setLineSelection([])}
                    onStateChange={handleDecisionState}
                    canMark={canDecideLines}
                    canApply={canDecideLines}
                    onApplied={() => updateList()}
                  />

                  <div className="claims-items-list">
                    <div className="claims-items-cols">
                      <div className="claims-items-cols__select">
                        <Checkbox
                          checked={allDecidableSelected}
                          indeterminate={lineSelection.length > 0 && !allDecidableSelected}
                          disabled={!canDecideLines || !decidableLineIds.length}
                          onChange={(e) => setLineSelection(e.target.checked ? decidableLineIds : [])}
                        />
                      </div>
                      <div className="claims-items-cols__main">Item</div>
                      <div className="claims-items-cols__money">Filed</div>
                      <div className="claims-items-cols__money">PA approved</div>
                      <div className="claims-items-cols__money">Claims approved</div>
                      <div className="claims-items-cols__actions">Actions</div>
                    </div>
                    {decoratedItems?.map((item, index) => (
                      <AuthorizationRequestDataListHook
                        data={item}
                        index={index}
                        itemFlags={itemFlagState.byItem[item?.id_] || []}
                        user={user}
                        claim={response}
                        viewOnly={canEdit}
                        misc={client}
                        encounter={encounter}
                        calback={() => updateList()}
                        selectable={canDecideLines}
                        selected={lineSelection.includes(item?.id_)}
                        onToggleSelect={toggleLineSelection}
                        key={index}
                      />
                    ))}
                  </div>
                </DashboardSection>

              </div>

              <div className="claims-review-aside">
                <div className="claims-waterfall" aria-label="Financial summary">
                  <div className="claims-waterfall__title">Financial summary</div>

                  <div className="claims-waterfall__stage">
                    <span className="claims-waterfall__stage-label">Requested</span>
                    <span className="claims-waterfall__stage-value">
                      <CurrencyFormat value={requestedCost} displayType="text" thousandSeparator prefix="₦" decimalScale={2} fixedDecimalScale />
                    </span>
                  </div>
                  <div className="claims-waterfall__connector">
                    {roundMoney(requestedCost - paApprovedCost) !== 0 ? (
                      <span className="claims-waterfall__connector-delta">
                        {requestedCost - paApprovedCost > 0 ? '−' : '+'}
                        <CurrencyFormat value={Math.abs(requestedCost - paApprovedCost)} displayType="text" thousandSeparator prefix="₦" decimalScale={2} fixedDecimalScale />
                        {' '}at PA review
                      </span>
                    ) : null}
                  </div>

                  <div className="claims-waterfall__stage">
                    <span className="claims-waterfall__stage-label">PA approved</span>
                    <span className="claims-waterfall__stage-value">
                      <CurrencyFormat value={paApprovedCost} displayType="text" thousandSeparator prefix="₦" decimalScale={2} fixedDecimalScale />
                    </span>
                  </div>
                  <div className="claims-waterfall__connector">
                    {approvedCostD === 0 && Number(response?.claims_status) < 2 ? (
                      <span className="claims-waterfall__connector-delta">pending claims review</span>
                    ) : roundMoney(paApprovedCost - approvedCostD) !== 0 ? (
                      <span className="claims-waterfall__connector-delta">
                        {paApprovedCost - approvedCostD > 0 ? '−' : '+'}
                        <CurrencyFormat value={Math.abs(paApprovedCost - approvedCostD)} displayType="text" thousandSeparator prefix="₦" decimalScale={2} fixedDecimalScale />
                        {' '}at claims review
                      </span>
                    ) : null}
                  </div>

                  <div className="claims-waterfall__stage">
                    <span className="claims-waterfall__stage-label">Claims approved</span>
                    <span className="claims-waterfall__stage-value">
                      <CurrencyFormat value={approvedCostD} displayType="text" thousandSeparator prefix="₦" decimalScale={2} fixedDecimalScale />
                    </span>
                  </div>

                  <div className="claims-waterfall__divider" />

                  <div className="claims-waterfall__variance">
                    <span className="claims-waterfall__variance-label">Variance (PA − effective)</span>
                    <span className={`claims-waterfall__variance-value ${varianceCost >= 0 ? 'is-positive' : 'is-negative'}`}>
                      <CurrencyFormat value={varianceCost} displayType="text" thousandSeparator prefix="₦" decimalScale={2} fixedDecimalScale />
                    </span>
                  </div>
                </div>
                {/* Consumption and benefit mapping sit under the financial summary, as on the PA page. */}
                <PmMappingPanel checkinCode={params.get("encounter")} source="claims" title="Consumption & benefit mapping" />

                {!loading && (
                  <div className="claims-workflow-card">
                    <div className="claims-workflow-card__title">Workflow</div>
                    <div className="claims-workflow-card__hint">Signed in as {workflowRoleLabel} · {claimStatusLabel}</div>
                    <div className="claims-workflow-card__actions">
                      {canProcessClaims ? (
                        <>
                          <Button
                            type="primary"
                            onClick={() => setVisibleApproval(true)}
                            disabled={
                              Number(response?.source)
                                ? true
                                : approvedCostD <= 0 || Number(response?.claims_status) > 0 || sending
                            }
                          >
                            Submit to HM
                          </Button>
                          {(response?.medical_complaint_hm !== '' || response?.claims_comment !== '' || items?.some(item => item?.claim_comment)) ? (
                            <Button
                              type="default"
                              icon={<MessageOutlined />}
                              onClick={() => setVisibleMedicalComplaints(true)}
                            >
                              View comments
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                      {canApproveClaims || canRejectClaims ? (
                        <>
                          {canApproveClaims && !items?.filter(i => [0].includes(Number(i?.claim_flag)))?.length ? (
                            <Button
                              type="primary"
                              onClick={send_claim_finance}
                              disabled={
                                Number(response?.claims_status) < 1
                                || submittedCFO
                                || Number(response?.claims_status) > 1
                                || sending
                              }
                            >
                              Approve
                            </Button>
                          ) : null}
                          {canRejectClaims ? (
                            <Button
                              type="primary"
                              danger
                              disabled={
                                Number(response?.claims_status) < 1
                                || submittedCFO
                                || Number(response?.claims_status) > 1
                              }
                              onClick={() => setVisibleReject(true)}
                            >
                              Reject
                            </Button>
                          ) : null}
                          {(response?.medical_complaint_hm !== '' || response?.claims_comment !== '' || items?.some(item => item?.claim_comment)) ? (
                            <Button
                              type="default"
                              icon={<MessageOutlined />}
                              onClick={() => setVisibleMedicalComplaints(true)}
                            >
                              View comments
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                      {isAdministrator && revertInfo ? (
                        <>
                          <Tooltip title={revertInfo.revertible ? 'Undo the submission: the claim is withdrawn and the encounter reopens at the facility' : null}>
                            <Button
                              danger
                              icon={<RollbackOutlined />}
                              disabled={!revertInfo.revertible || sending}
                              onClick={() => setRevertOpen(true)}
                            >
                              Revert to encounter
                            </Button>
                          </Tooltip>
                          {/* A disabled button fires no hover events, so a tooltip
                              would never show why — state it inline instead. */}
                          {!revertInfo.revertible && revertInfo.reason ? (
                            <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
                              Can&apos;t revert: {revertInfo.reason}
                            </Typography.Text>
                          ) : null}
                        </>
                      ) : null}
                      <Button onClick={goBack}>Close</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Modal
        title="Revert this claim to an open encounter?"
        open={revertOpen}
        onOk={submitRevert}
        onCancel={() => { if (!reverting) { setRevertOpen(false); setRevertReason(''); } }}
        okText="Revert claim"
        okButtonProps={{ danger: true, disabled: revertReason.trim().length < 5 }}
        confirmLoading={reverting}
        destroyOnClose
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert
            type="warning"
            showIcon
            message="This undoes the checkout"
            description="The claim is withdrawn and the encounter reopens at the facility, which can then check it out and submit it again. PA items, approvals and documents are kept. The final diagnosis entered at submission is cleared."
          />
          <div>
            <Typography.Text strong>Reason</Typography.Text>
            <TextArea
              rows={3}
              value={revertReason}
              onChange={(e) => setRevertReason(e.target.value)}
              placeholder="e.g. Facility submitted against the wrong encounter"
              maxLength={500}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Recorded in the activity log with your name.
            </Typography.Text>
          </div>
        </Space>
      </Modal>
      <Modal
        title={
          <div
            style={{
              width: "100%",
              cursor: "move",
            }}
            onMouseOver={() => {
              if (disabled) {
                setDisabled(false);
              }
            }}
            onMouseOut={() => {
              setDisabled(true);
            }}
            onFocus={() => { }}
            onBlur={() => { }} // end
          >
            Claim Approval Memo
          </div>
        }
        open={visibleApproval}
        onOk={send_claim_medical}
        onCancel={handleCancel}
        destroyOnClose
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        modalRender={(modal) => (
          <Draggable
            disabled={disabled}
            bounds={bounds}
            onStart={(event, uiData) => onStart(event, uiData)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )}
        okText={"Submit"}
      >
        <TextArea rows={5} onChange={(e) => setApprovalMemo(e.target.value)} />
      </Modal>
      <Modal
        title={
          <div
            style={{
              width: "100%",
              cursor: "move",
            }}
            onMouseOver={() => {
              if (disabled) {
                setDisabled(false);
              }
            }}
            onMouseOut={() => {
              setDisabled(true);
            }}
            onFocus={() => { }}
            onBlur={() => { }} // end
          >
            Rejection Memo
          </div>
        }
        open={visibleReject}
        onOk={reject}
        onCancel={handleCancel}
        destroyOnClose
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        modalRender={(modal) => (
          <Draggable
            disabled={disabled}
            bounds={bounds}
            onStart={(event, uiData) => onStart(event, uiData)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )}
        okText={"Reject"}
      >
        <TextArea rows={5} onChange={(e) => setRejectionMemo(e.target.value)} />
      </Modal>
      <Modal
        title={
          <div
            style={{
              width: "100%",
              cursor: "move",
            }}
            onMouseOver={() => {
              if (disabled) {
                setDisabled(false);
              }
            }}
            onMouseOut={() => {
              setDisabled(true);
            }}
            onFocus={() => { }}
            onBlur={() => { }} // end
          >
            Claim Comments & Memos
          </div>
        }
        open={visibleMedicalComplaints}
        onCancel={handleCancel}
        onOk={handleCancel}
        destroyOnClose
        width={600}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        modalRender={(modal) => (
          <Draggable
            disabled={disabled}
            bounds={bounds}
            onStart={(event, uiData) => onStart(event, uiData)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )}
      >
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {/* Approval Comments */}
          {ApprovalComplaints?.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <Typography.Paragraph type="success" strong style={{
                fontSize: '14px',
                marginBottom: '8px',
                padding: '8px 12px',
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px'
              }}>
                ✅ Approval Comments
              </Typography.Paragraph>
              {ApprovalComplaints.map((element, index) => (
                <div key={index} style={{
                  padding: '8px 12px',
                  background: '#f9f9f9',
                  border: '1px solid #e8e8e8',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  fontSize: '13px'
                }}>
                  {element.replace(">>>", "→")}
                </div>
              ))}
            </div>
          )}

          {/* Rejection Comments */}
          {medicalComplaints?.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <Typography.Paragraph type="danger" strong style={{
                fontSize: '14px',
                marginBottom: '8px',
                padding: '8px 12px',
                background: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '6px'
              }}>
                ❌ Rejection Comments
              </Typography.Paragraph>
              {medicalComplaints.map((element, index) => (
                <div key={index} style={{
                  padding: '8px 12px',
                  background: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  fontSize: '13px'
                }}>
                  {element.replace(">>>", "→")}
                </div>
              ))}
            </div>
          )}

          {/* PA Batch Comment - Facility Comment */}
          <PACommentsDisplay 
            response={response} 
            encounter={encounter}
            showFullHistory={true}
            compact={false}
          />

          {/* General Claims Comments */}
          {/* {response?.claims_comment && response.claims_comment !== "" && (
            <div style={{ marginBottom: '20px' }}>
              <Typography.Paragraph strong style={{
                fontSize: '14px',
                marginBottom: '8px',
                padding: '8px 12px',
                background: '#f0f5ff',
                border: '1px solid #adc6ff',
                borderRadius: '6px'
              }}>
                💬 General Comments
              </Typography.Paragraph>
              {response.claims_comment.split("|break|").map((comment, index) => (
                comment.trim() && (
                  <div key={index} style={{
                    padding: '8px 12px',
                    background: '#fafafa',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    fontSize: '13px'
                  }}>
                    {comment.replace(">>>", "→")}
                  </div>
                )
              ))}
            </div>
          )} */}

          {/* Individual Item Comments */}
          {items?.filter(item => item?.claim_comment && item.claim_comment !== "")?.length > 0 && (
            <div>
              <Typography.Paragraph strong style={{
                fontSize: '14px',
                marginBottom: '8px',
                padding: '8px 12px',
                background: '#fffbe6',
                border: '1px solid #ffe58f',
                borderRadius: '6px'
              }}>
                📝 Item-Specific Comments
              </Typography.Paragraph>
              {items.filter(item => item?.claim_comment && item.claim_comment !== "").map((item, index) => (
                <div key={index} style={{ marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#595959',
                    marginBottom: '4px'
                  }}>
                    {paLineItemName(item)}
                  </div>
                  <div style={{
                    padding: '8px 12px',
                    background: '#fff7e6',
                    border: '1px solid #ffd591',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}>
                    {item.claim_comment}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Comments Available */}
          {!ApprovalComplaints?.length &&
            !medicalComplaints?.length &&
            (!response?.claims_comment || response.claims_comment === "") &&
            !items?.some(item => item?.claim_comment && item.claim_comment !== "") &&
            !(response?.batch_comment || response?.pa_batch_comment || response?.pa_request_comment || encounter?.pa_comment || encounter?.batch_comment) && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#8c8c8c'
              }}>
                <MessageOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                <div>No comments available for this claim</div>
              </div>
            )}
        </div>
      </Modal>
    </>
  );
};

// Mirrors ClaimCheckoutItemService::REMOVAL_REASONS on the API.
const REMOVAL_REASON_LABELS = {
  service_not_rendered: 'Service not rendered',
  client_refused_care: 'Client refused care',
  never_administered: 'Not administered',
};

const AuthorizationRequestDataListHook = ({ data, claim, misc, encounter, user, calback = () => { }, selectable = false, selected = false, onToggleSelect = () => { }, ...props }) => {
  const [lineFlagsOpen, setLineFlagsOpen] = useState(false);
  const { hasAnyPermissionWithLegacy } = usePermissions();
  // Claims-item stage capabilities; legacy ids apply only while the RBAC map is empty.
  const canProcessClaimItems = hasAnyPermissionWithLegacy(['claims.process'], [3]);
  const canRejectClaimItems = hasAnyPermissionWithLegacy(['claims.reject'], [4]);
  // Money fields on the item row stay editable for the claims-review stage only.
  const canEditClaimAmounts = hasAnyPermissionWithLegacy(['claims.process', 'claims.update'], [1, 3]);
  // The head-of-medicals persona knocks a line straight back to pending; the claims
  // officer must record a rejection memo. The negative clause keeps a super admin
  // (admin.* matches claims.reject too) on the memo branch, as today.
  const isMedicalReviewerStage = canRejectClaimItems
    && !hasAnyPermissionWithLegacy(['claims.process'], [3]);
  // TODO(rbac-migration): under the corrected map role 3 is the Claims Officer, whose
  // queue is segment 0 (Pending CO), so sending them to segment 1 looks inverted. The
  // behaviour is preserved verbatim here; fixing it is a separate product change.
  const canCreateItemFlags = hasAnyPermissionWithLegacy(['flags.create', 'preauth.flag'], [1, 2]);
  const workflowSegmentFallback = canProcessClaimItems
    && !hasAnyPermissionWithLegacy(['claims.approve', 'claims.review'], [4])
    ? 1
    : 0;
  const dispatcher = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const [, setSending] = useState(false);
  const [, setVisibleQuery] = useState(false);
  const [visibleReject, setVisibleReject] = useState(false);
  const [visibleApproval, setVisibleApproval] = useState(false);
  const [comment, setComment] = useState("");
  const [processed, setProcessed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [claimData, setClaimData] = useState({ 
    ...(data || {}),
    __initialCost: data?.cost,
    cost: roundMoney(data?.cost),
    pa_approved_cost: roundMoney(data?.pa_approved_cost ?? data?.approved_cost ?? 0),
    claims_approved_cost: data?.claims_approved_cost != null ? roundMoney(data.claims_approved_cost) : null,
  });
  // NOTE: the second [1, 3] is data.claim_flag (approved / rejected), not a role id.
  const [viewOnly, _] = useState(() =>
    !canEditClaimAmounts ||
    (!Number(encounter?.source) &&
      [1, 3].includes(Number(data?.claim_flag)))
  );

  // Modal control start
  const [disabled, setDisabled] = useState(false);

  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });

  const draggleRef = useRef(null);

  // Use claims flag to determine if this item is locked for edits
  const claimFlag = claimData?.claim_flag;
  const isItemLocked = Number(claimFlag) && Number(claimFlag) !== 2;
  const isApprovedFlag = Number(data?.claim_flag) === 1;
  const isRejectedFlag = Number(data?.claim_flag) === 3;
  // Removed by the facility before submit (service not rendered / client
  // refused care): kept on the claim for the audit trail, never actionable.
  // The server refuses any action on it too; this only makes that visible.
  const isRemoved = Boolean(data?.removed_by_facility) || Number(data?.not_administered) === 1;
  const removalLabel = data?.removal_reason_label
    || REMOVAL_REASON_LABELS[data?.not_administered_reason]
    || data?.not_administered_reason
    || '';
  const hasDocs = Boolean(
    data?.doc || data?.has_documents || (Array.isArray(data?.doc_urls) && data.doc_urls.length)
  );
  const docCount = Array.isArray(data?.doc_urls) && data.doc_urls.length
    ? data.doc_urls.length
    : Array.isArray(data?.doc_files) && data.doc_files.length
      ? data.doc_files.length
      : String(data?.doc || '').split('|').map((s) => s.trim()).filter(Boolean).length || (hasDocs ? 1 : 0);

  const showApprovalComment = () => {
    setVisibleApproval(true);
  };

  const showRejectComment = () => {
    if (isMedicalReviewerStage) {
      handleOkReject(0);
    } else {
      setVisibleReject(true);
    }
  };

  async function submitAction(payload) {
    return myRequestObj(`claimItemApprove`)
      .post(payload)
      .then((response) => {
        return response?.data;
      })
      .then((body) => {
        return body;
      })
      .catch((error) => {
        throw error;
      });
  }

  const handleOkReject = (e = 3) => {
    // Extract only primitive values to avoid DOM elements and React components
    let safeId = null;
    if (typeof claimData.id_ === 'string' || typeof claimData.id_ === 'number') {
      safeId = claimData.id_;
    } else if (claimData.id_ && typeof claimData.id_ === 'object') {
      // If it's an object, try to extract a meaningful ID
      safeId = claimData.id_.id || claimData.id_.value || claimData.id_.textContent || null;
    }

    const safeComment = typeof comment === 'string' ? comment : '';

    // Create a clean payload with only primitive serializable data
    const cleanPayload = {
      id_: safeId,
      claim_flag: e, // This should be a number, not a DOM element
      claim_comment: safeComment
    };

    // console.log('Clean payload before sending:', cleanPayload);
    // console.log('Can serialize cleanPayload:', canSerialize(cleanPayload));
    // if (!canSerialize(cleanPayload)) {
    //   console.log('Problematic properties in cleanPayload:', findProblematicProperties(cleanPayload));
    // }

    // try {
    //   console.log('Payload JSON test:', JSON.stringify(cleanPayload));
    // } catch (error) {
    //   console.error('JSON.stringify failed for cleanPayload:', error);
    // }

    setProcessing(true);

    // Only update with serializable data to prevent circular references
    setClaimData(d => {
      const cleanData = { ...d };
      // Remove any potential React/DOM references
      delete cleanData.__reactFiber$;
      delete cleanData.__reactInternalInstance$;
      delete cleanData._reactInternalInstance;
      delete cleanData._reactInternalFiber;

      const mergedData = { ...cleanData, ...cleanPayload };

      return mergedData;
    });

    // Send only the clean payload to prevent circular references
    submitAction(cleanPayload).then((result) => {
      if (result?.error) {
        notification.error({
          message: result?.message || 'Rejection failed',
          placement: "topLeft",
        });
        setProcessing(false);
        setVisibleReject(false);
      } else {
        notification.success({
          message: result?.message || 'Item rejected successfully',
          placement: "topLeft",
        });
        // Call the callback to refresh the list
        if (calback) calback();
        setVisibleReject(false);
        setProcessing(false);
        setProcessed(true);
        
        // Stay on the filing page after item rejection.
        // If a workflow needs "return to list", pass:
        // navigate('/claims/filings/...', { state: { ..., returnToClaimsListOnItemAction: true }})
        const month = state?.returnToClaimsListOnItemAction ? state?.month : null;
        
        // Helper to get facility ID from various formats
        const getFacilityId = (fac) => {
          if (!fac) return null;
          if (typeof fac === 'object') return fac.facility_id || fac.id || null;
          return fac;
        };
        
        // Helper to get month key from various formats
        const getMonthKey = (m) => {
          if (!m) return null;
          if (typeof m === 'string') return m;
          return m.month || m.key || null;
        };
        
        const facility = state?.facility || window.__claimsFilingFacility;
        const facilityId = getFacilityId(facility);
        const monthKey = getMonthKey(month);
        
        // Use the segment from state (where we came from)
        // Rejecting items should keep the reviewer in the queue they came from;
        // only forwarding the entire claim changes the segment.
        // Seeded staff role map: 1 Super User, 3 Claims Officer, 4 Head Of Medicals.
        let currentSegment = state?.segment;

        // If segment not in state, fall back to the queue this reviewer works from.
        if (currentSegment === undefined || currentSegment === null) {
          currentSegment = workflowSegmentFallback;
        }
        
        // Only navigate back to list if the caller explicitly requested it via state
        // (e.g. entire-claim flows). Item-level rejects normally stay on this page.
        if (month && monthKey && facilityId !== null && facilityId !== undefined) {
          try {
            // Navigate back to Payment/Admin UI with URL parameters, staying in the same segment
            navigate(`/claims?status=${currentSegment}&month=${monthKey}&facility=${facilityId}`);
            return;
          } catch (navError) {
            console.error('Navigation error after rejection:', navError);
            // If navigation fails, just stay on the current filing page
          }
        }
      }
    }).catch((error) => {
      notification.error({
        message: 'Error',
        description: error?.message || 'Failed to reject item',
        placement: "topLeft",
      });
      setProcessing(false);
      setVisibleReject(false);
    });

    // Strip out any local-only fields before dispatching
    const { __initialCost, ...approvedPayload } = claimData || {};
    dispatcher(add_approved(approvedPayload));
    setSending(true);
  };

  const handleOkApprove = (e) => {
    // Create a clean payload with only serializable data
    const cleanPayload = {
      id_: claimData.id_,
      claim_flag: 1,
      claim_comment: comment,
      cost: roundMoney(claimData.cost),
      // Compatibility: old backends still read approved_cost for claims-stage approvals.
      approved_cost: roundMoney(claimData.claims_approved_cost ?? claimData.approved_cost),
      claims_approved_cost: roundMoney(claimData.claims_approved_cost),
    };

    setProcessing(true);

    // Only update with serializable data to prevent circular references
    setClaimData(d => {
      const cleanData = { ...d };
      // Remove any potential React/DOM references
      delete cleanData.__reactFiber$;
      delete cleanData.__reactInternalInstance$;
      delete cleanData._reactInternalInstance;
      delete cleanData._reactInternalFiber;
      return { ...cleanData, ...cleanPayload };
    });

    // Send only the clean payload to prevent circular references
    submitAction(cleanPayload).then((result) => {
      if (result?.error) {
        notification.error({
          message: result?.message || 'Approval failed',
          placement: "topLeft",
        });
        setProcessing(false);
      } else {
        notification.success({
          message: result?.message || 'Item approved successfully',
          placement: "topLeft",
        });
        // Call the callback to refresh the list
        if (calback) calback();
        
        // Stay on the filing page after item approval.
        // If a workflow needs "return to list", pass:
        // navigate('/claims/filings/...', { state: { ..., returnToClaimsListOnItemAction: true }})
        const month = state?.returnToClaimsListOnItemAction ? state?.month : null;
        if (month) {
          // Helper to get facility ID from various formats
          const getFacilityId = (fac) => {
            if (!fac) return null;
            if (typeof fac === 'object') return fac.facility_id || fac.id || null;
            return fac;
          };
          
          // Helper to get month key from various formats
          const getMonthKey = (m) => {
            if (!m) return null;
            if (typeof m === 'string') return m;
            return m.month || m.key || null;
          };
          
          const facility = state?.facility || window.__claimsFilingFacility;
          const facilityId = getFacilityId(facility);
          const monthKey = getMonthKey(month);
          
          // Use the segment from state (where we came from)
          // Approving items should keep the reviewer in the queue they came from;
          // only forwarding the entire claim changes the segment.
          // Seeded staff role map: 1 Super User, 3 Claims Officer, 4 Head Of Medicals.
          let currentSegment = state?.segment;

          // If segment not in state, fall back to the queue this reviewer works from.
          if (currentSegment === undefined || currentSegment === null) {
            currentSegment = workflowSegmentFallback;
          }
          
          if (monthKey && facilityId !== null && facilityId !== undefined) {
            // Navigate back to Payment/Admin UI with URL parameters, staying in the same segment
            navigate(`/claims?status=${currentSegment}&month=${monthKey}&facility=${facilityId}`);
            return;
          }
        }
      }
    }).catch((error) => {
      notification.error({
        message: 'Error',
        description: error?.message || 'Failed to approve item',
        placement: "topLeft",
      });
      setProcessing(false);
    });
    
    dispatcher(add_approved(cleanPayload));
    setSending(true);
    setVisibleApproval(false);
    setProcessing(false);
    setProcessed(true);
  };

  const handleCancel = (e) => {
    setVisibleApproval(false);
    setVisibleQuery(false);
    setVisibleReject(false);
  };

  const onStart = (_event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();

    if (!targetRect) {
      return;
    }

    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const lineFlags = props?.itemFlags || [];
  const activeLineFlags = lineFlags.filter((f) => f.status === 'active');
  const isLineFlagged = activeLineFlags.length > 0 || Boolean(data?.has_active_flags);

  return (
    <>
      <div
        className={`claims-item-row${
          isRemoved ? ' claims-item-row--removed' : isRejectedFlag ? ' claims-item-row--reject' : isApprovedFlag ? ' claims-item-row--accept' : isLineFlagged ? ' claims-item-row--flagged' : ''
        }${
          // A staged mark reads as a lighter version of the decision it will
          // become, so the reviewer can see what Apply is about to do.
          Number(data?.claim_mark) === 1 ? ' claims-item-row--marked-accept'
            : Number(data?.claim_mark) === 2 ? ' claims-item-row--marked-reject' : ''
        }`}
      >
        <div className="claims-item-select">
          {selectable ? (
            <Checkbox
              checked={selected}
              disabled={!data?.claim_decidable}
              onChange={() => onToggleSelect(data?.id_)}
              aria-label="Select line for a decision"
            />
          ) : null}
        </div>

        <div
          className={`claims-item-status-icon ${
            isRejectedFlag ? 'claims-item-status-icon--rejected' : isApprovedFlag ? 'claims-item-status-icon--approved' : 'claims-item-status-icon--pending'
          }`}
        >
          {isRejectedFlag ? <CloseOutlined /> : isApprovedFlag ? <CheckOutlined /> : '·'}
        </div>

        <div className="claims-item-main">
          <div className="claims-item-name-row">
            <span className="claims-item-sn">{String((props?.index ?? 0) + 1).padStart(2, '0')}</span>
            <span className="claims-item-name">{`${(data?.category_name || '').split(' ')[0]} - ${paLineItemName(data)}`}</span>
            {isRemoved ? (
              <span className="claims-item-removed" title="Removed by the facility before submission. It cannot be processed.">
                Removed by facility{removalLabel ? ` — ${removalLabel}` : ''}
              </span>
            ) : null}
            {hasDocs ? <span className="claims-item-doc">📎 {docCount}</span> : null}
            {activeLineFlags.length ? (
              <ItemFlagTag flags={lineFlags} onClick={() => setLineFlagsOpen(true)} />
            ) : data?.has_active_flags ? (
              <span className="claims-item-flag">
                {data?.flags_active_count || 1} flag{Number(data?.flags_active_count) === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
          {activeLineFlags.length ? (
            <div
              className="claims-item-flag-note"
              role="button"
              tabIndex={0}
              onClick={() => setLineFlagsOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter') setLineFlagsOpen(true); }}
              title="View the flags on this item"
            >
              <strong>{activeLineFlags[0].created_by_label ? `${activeLineFlags[0].created_by_label} flag` : 'Flag'}:</strong>{' '}
              {activeLineFlags[0].note}
              {activeLineFlags.length > 1 ? ` (+${activeLineFlags.length - 1} more)` : ''}
            </div>
          ) : null}
          <div className="claims-item-meta">
            Qty {data?.quantity}
            {Number(data?.claim_mark) === 1 ? (
              <span className="claims-item-mark claims-item-mark--accept">marked to accept</span>
            ) : null}
            {Number(data?.claim_mark) === 2 ? (
              <span className="claims-item-mark claims-item-mark--reject">
                marked to reject{data?.claim_mark_comment ? ` — ${data.claim_mark_comment}` : ''}
              </span>
            ) : null}
          </div>
        </div>

        <div className="claims-item-money">
          {renderField({
            viewOnly: viewOnly || isRemoved,
            label: null,
            // Disable editing when the originally loaded cost is non-zero
            isDisabled: viewOnly || isRemoved || Number(claimData?.__initialCost) !== 0,
            inputType: 'number',
            value: claimData?.cost,
            onChange: (e) => setClaimData((d) => ({ ...d, cost: roundMoney(e.target.value) }))
          })}
        </div>
        <div className="claims-item-money">
          <span className="claims-item-money__value">
            <CurrencyFormat
              value={roundMoney(claimData?.pa_approved_cost ?? claimData?.approved_cost ?? 0)}
              displayType={"text"}
              thousandSeparator={true}
              prefix={"₦"}
              decimalScale={2}
              fixedDecimalScale
            />
          </span>
        </div>
        <div className="claims-item-money">
          {renderField({
            viewOnly: viewOnly || isRemoved,
            label: null,
            isDisabled: viewOnly || isRemoved, //|| Number(claimData?.approved_cost) !== 0,
            inputType: 'number',
            value: claimData?.claims_approved_cost,
            onChange: (e) => setClaimData((d) => ({ ...d, claims_approved_cost: roundMoney(e.target.value) }))
          })}
        </div>

        <div className="claims-item-actions">
          {canProcessClaimItems && !isRemoved && <>
            <Button
              className="claims-item-icon-btn"
              type="default"
              icon={<CheckOutlined />}
              disabled={
                !(Number(claimData?.cost) && Number(claimData?.claims_approved_cost)) ||
                processed ||
                processing ||
                [1, 2, 3].includes(Number(claim?.claims_status)) ||
                [1, 3].includes(Number(claimData?.claim_flag))
              }
              onClick={showApprovalComment}
              title="Approve"
            />
            <Button
              className="claims-item-icon-btn"
              type="default"
              danger
              icon={<CloseOutlined />}
              disabled={
                processed ||
                processing ||
                [1, 2, 3].includes(Number(claim?.claims_status)) ||
                [1, 3].includes(Number(claimData?.claim_flag))
              }
              onClick={showRejectComment}
              title="Reject"
            />
          </>}
          {canRejectClaimItems && !isRemoved && <>
            <Button
              className="claims-item-icon-btn"
              type="default"
              danger
              icon={<CloseOutlined />}
              disabled={
                [0, 2, 3].includes(Number(claim?.claims_status)) ||
                (processed || processing) && ([0, 3].includes(Number(claimData?.claim_flag)))
              }
              onClick={showRejectComment}
              title="Reject"
            />
          </>}
          {hasDocs ? (
            <FileViewerComponent
              file={data?.doc}
              docUrls={data?.doc_urls}
              docFiles={data?.doc_files}
              haveFile={hasDocs}
              data={data}
            />
          ) : null}
          {/* PAItemFlagIndicator still derives `canCreateFlags` from
              `[1, 2].includes(Number(userRoleId))`; that file is migrated separately, so
              keep its prop contract and hand it a permission-derived stand-in (1 = may
              create flags, 0 = may not). flags.create is catalogued but seeded to no role,
              so preauth.flag — which legacy Customer Care (2) actually holds — stands in. */}
          <Modal
            title={`Flags · ${paLineItemName(data)}`}
            open={lineFlagsOpen}
            onCancel={() => setLineFlagsOpen(false)}
            footer={null}
            width={600}
          >
            <ItemFlagsList flags={lineFlags} emptyText="This item has not been flagged" />
          </Modal>
          <PAItemFlagIndicator
            itemId={data?.id_}
            userRoleId={canCreateItemFlags ? 1 : 0}
            initialFlagCount={data?.flags_active_count || 0}
            initialHasFlags={Boolean(data?.has_active_flags)}
          />
        </div>
      </div>
      <Modal
          title={
            <div
              style={{
                width: "100%",
                cursor: "move",
              }}
              onMouseOver={() => {
                if (disabled) {
                  setDisabled(false);
                }
              }}
              onMouseOut={() => {
                setDisabled(true);
              }}
              onFocus={() => { }}
              onBlur={() => { }} // end
            >
              Approval Memo
            </div>
          }
          open={visibleApproval}
          onOk={handleOkApprove}
          onCancel={handleCancel}
          destroyOnClose
          modalRender={(modal) => (
            <Draggable
              disabled={disabled}
              bounds={bounds}
              onStart={(event, uiData) => onStart(event, uiData)}
            >
              <div ref={draggleRef}>{modal}</div>
            </Draggable>
          )}
          okText={processing ? <Spin size={"small"} /> : "Approve"}
        >
          <div
            className="form-group"
            style={{ width: "100%", marginBottom: "5px" }}
          >
            <label className="form-label">Comment</label>
            <TextArea
              rows={3}
              style={{ width: "100%" }}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </Modal>

        <Modal
          title={
            <div
              style={{
                width: "100%",
                cursor: "move",
              }}
              onMouseOver={() => {
                if (disabled) {
                  setDisabled(false);
                }
              }}
              onMouseOut={() => {
                setDisabled(true);
              }}
              onFocus={() => { }}
              onBlur={() => { }} // end
            >
              Rejection Memo
            </div>
          }
          open={visibleReject}
          onOk={(e) => handleOkReject()}
          onCancel={handleCancel}
          destroyOnClose
          modalRender={(modal) => (
            <Draggable
              disabled={disabled}
              bounds={bounds}
              onStart={(event, uiData) => onStart(event, uiData)}
            >
              <div ref={draggleRef}>{modal}</div>
            </Draggable>
          )}
          okText={processing ? <Spin size={"small"} /> : "Reject"}
        >
          <div
            className="form-group"
            style={{ width: "100%", marginBottom: "5px" }}
          >
            <label className="form-label">Comment</label>
            <TextArea
              rows={3}
              style={{ width: "100%" }}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </Modal>
    </>
  );
};

function DebounceSelect({ fetchOptions, debounceTimeout = 800, ...props }) {
  const [fetching, setFetching] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const fetchRef = React.useRef(0);
  const debounceFetcher = React.useMemo(() => {
    const loadOptions = (value) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);
      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }
        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
}

// const ClientDataHook = (props) => {
//   const {data} = props;
//   const style = {
//     ...props?.style,
//     display: "flex",
//     color: "black",
//     fontSize: "1.3em",
//     textAlign: "left",
//     padding: ".2rem",
//     borderWidth: ".2em",
//     borderStyle: "solid",
//     borderColor: "rgba(0, 0, 0, .4)",
//     borderRadius: ".5em",
//   };

//   return (
//     <>
//       <Row className="card-c" style={style} align="middle">
//         <Col span={4} className="img-round img-medium">
//           <img
//             src={
//               data?.pic_name
//                 ? `${uri_img}${data?.pic_name}`
//                 : logo
//             }
//             alt=""
//             style={{
//               width: "120px",
//               height: "120px",
//               display: "flex",
//               alignSelf: "center",
//               justifySelf: "center",
//               margin: ".3em",
//             }}
//             className="rounded-circle"
//           />
//         </Col>
//         <Col
//           span={20}
//           className="card-c-details"
//           style={{
//             display: "flex",
//             flexDirection: "column",
//             alignSelf: "center",
//             justifySelf: "center",
//             paddingLeft: "30px",
//           }}
//         >
//           <Row className="card-c-datarow">
//             <Col span={10} className="card-c-label">
//               Patient Full Name:
//             </Col>
//             <Col className="card-c-value">
//               {" "}
//               {data?.surname
//                 ? data?.surname.toUpperCase()
//                 : ""}, {data?.first_name ? data?.first_name : ""}{" "}
//               {data?.middle_name ? data?.middle_name : ""}
//             </Col>
//           </Row>
//           <Row className="card-c-datarow">
//             <Col span={10} className="card-c-label">
//               Patient Insurance ID:
//             </Col>
//             <Col className="card-c-value">
//               {" "}
//               {data?.insurance_no ? data?.insurance_no : ""}
//             </Col>
//           </Row>
//           <Row className="card-c-datarow">
//             <Col span={10} className="card-c-label">
//               Patient Premium Type:
//             </Col>
//             <Col className="card-c-value">
//               {" "}
//               {data?.insurance_package
//                 ? data?.insurance_package
//                 : ""}{" "}
//               | {data?.premium_type ? data?.premium_type : ""}
//             </Col>
//           </Row>
//           <Row className="card-c-datarow">
//             <Col span={10} className="card-c-label">
//               Patient Premium Status:
//             </Col>
//             <Col className="card-c-value">
//               {" "}
//               {data?.status ? `Active` : "Expired"}
//             </Col>
//           </Row>
//         </Col>
//         <Row justify="end" style={{width: '100%'}}>
//           <Col
//             span={20}
//             className="card-c-details"
//             style={{
//               display: "flex",
//               flexDirection: "column",
//               alignSelf: "flex-end",
//               justifySelf: "end",
//               paddingLeft: "30px",
//             }}
//           >
//             <h4>Facility Of Encounter</h4>
//             <Row className="card-c-datarow">
//               <Col span={10} className="card-c-label">
//                 Facility Name:
//               </Col>
//               <Col className="card-c-value">
//                 {data?.facility_name?.__upper()}
//               </Col>
//             </Row>
//             <Row className="card-c-datarow">
//               <Col span={10} className="card-c-label">
//                 Facility Email:
//               </Col>
//               <Col className="card-c-value">
//                 {data?.facility_email}
//               </Col>
//             </Row>
//             <Row className="card-c-datarow">
//               <Col span={10} className="card-c-label">
//                 Facility Supported Plan:
//               </Col>
//               <Col className="card-c-value">
//                 {data?.facility_plan_type?.__capitalize()}
//               </Col>
//             </Row>
//             <Row className="card-c-datarow">
//               <Col span={10} className="card-c-label">
//                 State / City:
//               </Col>
//               <Col className="card-c-value">
//                 {data?.facility_state?.toUpperCase()}{" "}{data?.facility_city?.toUpperCase()}
//               </Col>
//             </Row>
//           </Col>
//         </Row>
//       </Row>
//     </>
//   );
// };

const PAQuery = ({ data, processed, claim }) => {
  const dispatcher = useDispatch();
  const queries = useSelector(selectQueries)?.filter(
    (i) => Number(i.marker) === data.id_ && Number(i.level) === 1
  );
  const [loading, setLoading] = useState(false);
  const [, setsending] = useState(false);
  const [visible, setVisible] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messageData] = useState({
    relation: "claim_items",
    marker: data?.id_,
    source: 0,
    level: 1,
    facility_type: 0,
  });
  const [, setFirstLoad] = useState(true);
  const [unreadQueriesCount, setUnreadQueriesCount] = useState(0);
  const [queried, setQueried] = useState(0);
  const [reading, setReading] = useState(false);

  let interval = useRef();
  let interval2 = useRef();

  useEffect(() => {
    const payload = { id_: data.id_, source: 1, level: 1 };
    myRequestObj(`queriesCount`)
      .post(payload)
      .then((res) => res?.data)
      .then((body) => {
        if (!body?.error) {
          setUnreadQueriesCount(body?.data[0]?.queris);
          setQueried(Number(body?.data[1]?.querid));
        }
      })
      .finally(() => {
        if (interval2.current) clearInterval(interval2.current);
        // let isFetching = false;
        interval2.current = setInterval(() => {
          if (!reading && window.location.pathname === "/claims-action") {
            myRequestObj(`queriesCount`)
              .post(payload)
              .then((res) => res?.data)
              .then((body) => {
                if (!body?.error) {
                  setUnreadQueriesCount(body?.data[0]?.queris);
                  setQueried(Number(body?.data[1]?.querid));
                }
              })
            // .finally(() => isFetching = false)
          }
        }, 2000);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);

    if (visible) {
      localStorage.setItem(config.key.q_reading, 1);
    }

    fetchQueries(visible).then((res) => {
      setLoading(false);
      updateQueries();
    });

    setReading(true);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const queriesToUpdate = queries?.filter((j) => !Number(j.status) && Number(j.source))
      .map((i) => {
        return {
          id: i.id,
        };
      });

    if (visible && queriesToUpdate?.length) {
      myRequestObj(`update_queries`)
        .post(queriesToUpdate)
        .then((res) => res?.data)
        .finally(setFirstLoad(false));
    }
  }, [visible, queries]);

  const handleOk = () => {
    setsending(true);
    const payload = { content: messageText, ...messageData };
    myRequestObj(`query`)
      .post(payload)
      .then((res) => res?.data)
      .then((body) => {
        if (!body?.error) {
          dispatcher(add_queries(body?.data));
        }
        setsending(false);
        setMessageText("");
      });
  };

  const handleCancel = () => {
    localStorage.setItem(config.key.q_reading, 0);
    clearInterval(interval.current);
    setVisible(false);
    setReading(false);
  };

  const contoller = () => {
    setVisible(true);
  };

  const composeMessage = (e) => {
    const mess = e.target.value;
    setMessageText(mess);
  };

  const fetchQueries = async (x) => {
    if (!x) return;
    return myRequestObj(`queries`)
      .post(messageData)
      .then((res) => res?.data)
      .then((body) => {
        if (!body?.error && body?.data?.length) {
          dispatcher(add_queries(body?.data));
        }
      });
  };

  const updateQueries = () => {
    interval.current = setInterval(() => fetchQueries(visible), 5000);
  };

  return (
    <>
      <Badge count={Number(unreadQueriesCount) || null}>
        <MessageOutlined
          style={{
            fontSize: "2em",
            fontWeight: "bolder",
            color:
              ([1, 2, 3].includes(Number(claim?.claims_status)) && queried) ||
                ([1, 3].includes(Number(data?.claim_flag)) && queried) ||
                Number(data?.claim_flag) < 1 ||
                Number(data?.claim_flag) === 2 ||
                (processed && queried)
                ? "#ff7875"
                : "grey",
          }}
          onClick={() =>
            ([1, 2, 3].includes(Number(claim?.claims_status)) && queried) ||
              ([1, 3].includes(Number(data?.claim_flag)) && queried) ||
              Number(data?.claim_flag) < 1 ||
              Number(data?.claim_flag) === 2 ||
              (processed && queried)
              ? contoller()
              : null
          }
          disabled={
            ([1, 2, 3].includes(Number(claim?.claims_status)) && queried) ||
              ([1, 3].includes(Number(data?.claim_flag)) && queried) ||
              Number(data?.claim_flag) < 1 ||
              Number(data?.claim_flag) === 2 ||
              (processed && queried)
              ? false
              : true
          }
          title="Query"
        />
      </Badge>
      <Drawer
        open={visible}
        title={
          <>
            <Typography.Title level={5} style={{ margin: "0 auto" }}>
              Query: {data?.checkin_id}
            </Typography.Title>
          </>
        }
        onClose={handleCancel}
        footer={[
          <Row justify="space-between" align="bottom">
            <Col span={20}>
              <TextArea
                disabled={
                  ([1, 2, 3].includes(Number(claim?.claims_status)) &&
                    queried) ||
                  ([1, 3].includes(Number(data?.claim_flag)) && queried) ||
                  (processed && queried)
                }
                rows={2}
                value={messageText}
                placeholder="Write Full Description of the Query...."
                onChange={composeMessage}
                id={"message"}
              />
            </Col>
            <Col>
              <Button
                key="back"
                onClick={handleOk}
                style={{ color: "skyblue", fontSize: "1em" }}
                disabled={
                  ([1, 2, 3].includes(Number(claim?.claims_status)) &&
                    queried) ||
                  ([1, 3].includes(Number(data?.claim_flag)) && queried) ||
                  (processed && queried) ||
                  !messageText.length
                }
              >
                {" "}
                Send{" "}
              </Button>
            </Col>
          </Row>,
        ]}
        width={500}
      >
        <Space
          gutter={[8, 16]}
          direction={"vertical"}
          id="chatContainer"
          style={{ width: "100%" }}
        >
          {loading ? (
            <Col>
              <Spin size="large" />
            </Col>
          ) : !queries?.length ? (
            <Empty description={<span>No Queries</span>} />
          ) : (
            <>
              {queries?.sort((a, b) => Date.parse(a?.date) - Date.parse(b?.date))
                .map((q, i) => (
                  <Row
                    id={`query_${i}`}
                    justify={!Number(q?.source) ? "start" : "end"}
                  >
                    <Col
                      span={16}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        lineHeight: "18px",
                        width: "fit-content",
                        color: `${!Number(q?.source) ? "green" : "red"}`,
                        border: `${!Number(q?.source)
                          ? "1px solid green"
                          : "1px solid red"
                          }`,
                        borderRadius: "10px",
                        padding: "10px",
                      }}
                    >
                      <p>{q?.content}</p>
                      <span
                        style={{ textAlign: "right", justifySelf: "flex-end" }}
                      >
                        {new Date(q?.date).toLocaleString()}
                      </span>
                    </Col>
                  </Row>
                ))}
            </>
          )}
        </Space>
      </Drawer>
    </>
  );
};

const FileViewerComponent = ({ data, haveFile, file, docUrls = [], docFiles = [] }) => {
  const dispatcher = useDispatch();
  const [, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [messageData] = useState({
    relation: "claim_items",
    marker: data.id_,
    source: 0,
    level: 1,
  });
  const [fileToView, setfileToView] = useState("");
  const [fileLabel, setFileLabel] = useState("");
  const filesFromPipe = String(file || "")
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);
  const files = (Array.isArray(docFiles) && docFiles.length ? docFiles : filesFromPipe).map(
    (path, idx) => ({
      key: Array.isArray(docUrls) && docUrls[idx] ? docUrls[idx] : path,
      path,
      label: String(path).split("/").pop() || `Document ${idx + 1}`,
    }),
  );

  let interval = useRef();

  useEffect(() => {
    setLoading(true);
    fetchQueries(visible).then((res) => {
      setLoading(false);
      updateQueries();
    });
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => {
    clearInterval(interval.current);
    setVisible(false);
  };

  const fetchQueries = async (x) => {
    if (!x) return;
    return myRequestObj(`queries`)
      .post(messageData)
      .then((res) => res?.data)
      .then((body) => {
        if (!body.error && body.data.length) {
          dispatcher(add_queries(body.data));
        }
      });
  };

  const updateQueries = () => {
    interval.current = setInterval(() => fetchQueries(visible), 5000);
  };

  const menuHandler = (e) => {
    const selected = files.find((f) => f.key === e.key) || { key: e.key, label: e.key };
    const resolved =
      resolvePaAttachmentUrl({ url: selected.key, file_path: selected.path }) ||
      selected.key;
    setfileToView(resolved);
    setFileLabel(selected.label || e.key);
    setVisible(true);
  };

  return (
    <>
      {haveFile ? (
        <Badge count={files.length}>
          <Dropdown
            overlay={
              <Menu
                items={files.map((entry) => ({
                  label: entry.label,
                  key: entry.key,
                }))}
                onClick={menuHandler}
              />
            }
          >
            <Button className="claims-item-icon-btn" type="default" title="Documents">
              <BiBookOpen style={{ fontSize: "13px" }} />
            </Button>
          </Dropdown>
        </Badge>
      ) : null}
      <Drawer
        open={visible}
        title={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {fileLabel || `Files for: ${data.auth_code}`}
            </Typography.Title>
            {fileToView ? (
              <Button
                type="link"
                size="small"
                href={fileToView}
                target="_blank"
                rel="noreferrer"
              >
                Open in new tab
              </Button>
            ) : null}
          </Space>
        }
        onClose={handleCancel}
        width={800}
      >
        {/\.(jpe?g|png|gif|webp|bmp)($|\?)/i.test(fileToView) ? (
          <img
            src={fileToView}
            alt={fileLabel || "Document"}
            style={{ maxWidth: "100%", display: "block", margin: "0 auto" }}
          />
        ) : (
          <iframe
            src={fileToView}
            height={"100%"}
            width={"100%"}
            title={`Document ${fileLabel}`}
          />
        )}
      </Drawer>
    </>
  );
};

export default ClaimsHook;
